import { useCallback, useMemo, useState } from "react";

import { useToast } from "@hightouch/htui";

import { useUser } from "src/contexts/user-context";
import {
  type AmpBoardProjectsQueryVariables,
  type AmpBoardUserFragment,
  type AmpWorkflowStatusFragment,
  useAmpBoardProjectsQuery,
  useAmpBoardGroupsQuery,
  useAmpBoardTagsQuery,
  useAmpBoardWorkflowsQuery,
  useMembersQuery,
} from "src/graphql";
import { usePersistedState } from "src/hooks/use-persisted-state";
import { TASK_PEOPLE_PARAMS } from "src/pages/projects/constants";
import {
  EMPTY_FILTERS,
  readFilters,
  readViewOptions,
  type TaskFilters,
  type TaskViewOptions,
} from "src/pages/projects/task-view";
import { useLocation, useSearchParams } from "src/router";

export type BoardMember = AmpBoardUserFragment & { id: string };

// The area's pages share these variables, so they share one cached list each.
export const OPEN_PROJECTS_VARIABLES: AmpBoardProjectsQueryVariables = {
  where: { deleted_at: { _is_null: true } },
};

export const ARCHIVED_PROJECTS_VARIABLES: AmpBoardProjectsQueryVariables = {
  where: { deleted_at: { _is_null: false } },
};

/** Every open project, newest activity first. */
export const useOpenProjects = () => {
  const { data, isLoading, error, refetch } = useAmpBoardProjectsQuery(
    OPEN_PROJECTS_VARIABLES,
  );
  const projects = useMemo(() => data?.amp_projects ?? [], [data]);
  return { projects, isLoading, error, refetch };
};

/** The workspace's members, for owner, member and reviewer pickers. */
export const useWorkspaceMembers = () => {
  const { workspace } = useUser();
  const { data, isLoading } = useMembersQuery(
    { workspaceId: workspace?.id },
    { enabled: Boolean(workspace?.id) },
  );

  const members = useMemo<BoardMember[]>(
    () =>
      (data?.all_memberships_v2 ?? [])
        .flatMap((membership) =>
          membership.user
            ? [
                {
                  id: String(membership.user.id),
                  name: membership.user.name,
                  email: membership.user.email,
                },
              ]
            : [],
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data],
  );

  const byId = useMemo(
    () => new Map(members.map((member) => [member.id, member])),
    [members],
  );

  return { members, byId, isLoading };
};

/** Lets a page showing a list's task counts refetch it on mount, since task writes don't refetch the list. */
type ListQueryOptions = { refetchOnMount?: boolean | "always" };

/** Every workflow in the workspace, with its statuses indexed by id. */
export const useWorkflows = (options?: ListQueryOptions) => {
  const { data, isLoading, error, refetch } = useAmpBoardWorkflowsQuery(
    undefined,
    options,
  );

  const workflows = useMemo(() => data?.amp_workflows ?? [], [data]);

  const statusesByWorkflow = useMemo(
    () =>
      new Map<string, AmpWorkflowStatusFragment[]>(
        workflows.map((workflow) => [workflow.id, workflow.statuses]),
      ),
    [workflows],
  );

  const statusById = useMemo(
    () =>
      new Map<string, AmpWorkflowStatusFragment>(
        workflows.flatMap((workflow) =>
          workflow.statuses.map((status) => [status.id, status] as const),
        ),
      ),
    [workflows],
  );

  return {
    workflows,
    statusesByWorkflow,
    statusById,
    isLoading,
    error,
    refetch,
  };
};

/** The groups with access to the workspace, with their members, for reviewer pickers. */
export const useGroups = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { workspace } = useUser();
  const { data, isLoading, error, refetch } = useAmpBoardGroupsQuery(
    { workspaceId: String(workspace?.id) },
    { enabled: enabled && Boolean(workspace?.id) },
  );
  const groups = useMemo(
    () =>
      (data?.user_groups ?? []).map((group) => ({
        id: String(group.id),
        name: group.name,
        // Every count and list of a group's members reads this, so they agree.
        members: group.members
          .flatMap((member): BoardMember[] =>
            member.user ? [{ ...member.user, id: String(member.user.id) }] : [],
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      })),
    [data],
  );
  return { groups, isLoading, error, refetch };
};

/** Every tag in the workspace, by name. */
export const useTags = (options?: ListQueryOptions) => {
  const { data, isLoading, error, refetch } = useAmpBoardTagsQuery(
    undefined,
    options,
  );
  const tags = useMemo(() => data?.amp_tags ?? [], [data]);
  return { tags, isLoading, error, refetch };
};

/** A toast for a board write that failed, carrying the server's reason. */
export const useBoardErrorToast = () => {
  const { toast } = useToast();
  return useCallback(
    (title: string, error: unknown) =>
      toast({
        id: `amp-board-error-${title}`,
        title,
        message: error instanceof Error ? error.message : "Try again.",
        variant: "error",
      }),
    [toast],
  );
};

/** A URL search param, so a list or board view survives reloads and links. */
export const useSearchParamState = <T extends string>(
  key: string,
  isValid: (value: string | null) => value is T,
  fallback: T,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const raw = searchParams.get(key);
  const value = isValid(raw) ? raw : fallback;

  const setValue = (next: T) => {
    const params = new URLSearchParams(searchParams);
    if (next === fallback) params.delete(key);
    else params.set(key, next);
    setSearchParams(params, { state: location.state });
  };

  return [value, setValue] as const;
};

/**
 * A view's display options and filters, remembered per viewer and per view.
 * People in the URL win over the remembered filters until the filters change.
 */
export const useTaskViewState = (
  storageKey: string,
  defaults: TaskViewOptions,
  defaultFilters: TaskFilters,
) => {
  const storedOptions = usePersistedState<unknown>(
    `amp-board:${storageKey}:options`,
    defaults,
  );
  const storedFilters = usePersistedState<unknown>(
    `amp-board:${storageKey}:filters`,
    defaultFilters,
  );
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // Joined, so the memo below compares strings rather than new arrays.
  const linkedAssignees = searchParams
    .getAll(TASK_PEOPLE_PARAMS.assigneeIds)
    .join(",");
  const isLinked = linkedAssignees !== "";
  // Stable references, so memoized rows skip re-rendering when nothing changed.
  const options = useMemo(
    () => readViewOptions(storedOptions.value, defaults),
    [storedOptions.value, defaults],
  );
  const filters = useMemo(() => {
    if (isLinked) {
      return {
        ...EMPTY_FILTERS,
        assigneeIds: linkedAssignees.split(",").filter(Boolean),
      };
    }
    return storedFilters.value === undefined
      ? defaultFilters
      : readFilters(storedFilters.value);
  }, [isLinked, linkedAssignees, storedFilters.value, defaultFilters]);

  const setFilters = (next: TaskFilters) => {
    storedFilters.set(next);
    if (isLinked) {
      const params = new URLSearchParams(searchParams);
      params.delete(TASK_PEOPLE_PARAMS.assigneeIds);
      setSearchParams(params, { replace: true, state: location.state });
    }
  };

  return {
    options,
    setOptions: (next: TaskViewOptions) => storedOptions.set(next),
    resetOptions: () => storedOptions.set(defaults),
    filters,
    setFilters,
    search,
    setSearch,
  };
};
